import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  contactSchema,
  submitContactForm,
  type ContactFormValues,
} from '@/lib/contact-schema';

export function ContactForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onBlur',
    defaultValues: { name: '', email: '', message: '' },
  });

  const mutation = useMutation({
    mutationFn: submitContactForm,
    onSuccess: () => reset(),
  });

  const busy = isSubmitting || mutation.isPending;

  return (
    <form
      noValidate
      onSubmit={handleSubmit((values) => mutation.mutateAsync(values).catch(() => undefined))}
      className="space-y-6"
    >
      <Field
        id="name"
        label="Your name"
        error={errors.name?.message}
        input={
          <Input
            id="name"
            autoComplete="name"
            placeholder="Alex Rivera"
            aria-invalid={errors.name ? 'true' : 'false'}
            aria-describedby={errors.name ? 'name-error' : undefined}
            {...register('name')}
          />
        }
      />

      <Field
        id="email"
        label="Email"
        hint="Used only to reply to your message."
        error={errors.email?.message}
        input={
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email')}
          />
        }
      />

      <Field
        id="message"
        label="Message"
        hint="Include device and iOS version for bug reports."
        error={errors.message?.message}
        input={
          <Textarea
            id="message"
            placeholder="Describe what happened or what you need help with."
            aria-invalid={errors.message ? 'true' : 'false'}
            aria-describedby={errors.message ? 'message-error' : undefined}
            {...register('message')}
          />
        }
      />

      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {busy ? 'Sending…' : 'Send message'}
        </Button>
        <p className="text-sm text-muted-foreground">We usually reply within a couple of days.</p>
      </div>

      <div aria-live="polite" className="min-h-[1.5rem]">
        {mutation.isSuccess ? (
          <p className="flex items-start gap-2 rounded-md border border-leaf/40 bg-leaf/10 px-4 py-3 text-sm text-foreground">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-leaf" aria-hidden="true" />
            Thanks. Your message is on its way. We&rsquo;ll reply to your email address.
          </p>
        ) : null}

        {mutation.isError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Something went wrong. Please try again.'}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  input,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <Label htmlFor={id}>{label}</Label>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      {input}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
